import api from '../services/api';

export const getLeedsInAutomationReport = (): Promise<string> => {
    return api.post(`/report/leadsInAutomation`, {}).then(({ data }) => data);
};

export const getLeadsPerDayReport = (
    dateFrom: string,
    dateTo: string,
    queuesIds: number[] | string[],
    userId: number | null
): Promise<string> => {
    return api
        .post(`/report/leadsUserPerDay`, {
            dateFrom,
            dateTo,
            queuesIds,
            userId
        })
        .then(({ data }) => data);
};

export const getLeadsContactsPerDayReport = (
    dateFrom: string,
    dateTo: string,
    queuesIds: number[] | string[],
    userId: number | null
): Promise<Blob> => {
    return api
        .post(
            `/report/leadsContactsPerDay`,
            { dateFrom, dateTo, queuesIds, ...(userId ? { userId } : {}) },
            { responseType: 'blob' }
        )
        .then(({ data }) => data);
};

export const getLeadsAwaitingReport = (
    dateFrom: string,
    dateTo: string
): Promise<string> => {
    return api
        .post(`/report/leadsTotalAwaiting`, { dateFrom, dateTo })
        .then(({ data }) => data);
};

export const getLeadsAwaitingWorkingReport = (
    dateFrom: string,
    dateTo: string
): Promise<string> => {
    return api
        .post(`/report/leadsTotalAwaitingWorking`, { dateFrom, dateTo })
        .then(({ data }) => data);
};

export const getUsersAnalyticsReport = (
    dateFrom: string,
    dateTo: string,
    userId?: number
): Promise<string> => {
    return api
        .post(`/report/usersAnalytics`, { dateFrom, dateTo, userId })
        .then(({ data }) => data);
};

export const getTicketsClosedReasonsReport = (
    dateFrom: string,
    dateTo: string,
    userId?: number
): Promise<string> => {
    return api
        .post(`/report/ticketsClosedReasons`, { dateFrom, dateTo, userId })
        .then(({ data }) => data);
};

export const getContactsWithoutTicketReport = (
    limit: number
): Promise<string> => {
    return api
        .post(`/report/contactsWithoutTicket`, { limit })
        .then(({ data }) => data);
};

export const getTicketsWithMessagesReport = (
    dateFrom: string,
    dateTo: string,
    whatsappIds?: number[]
): Promise<string> => {
    return api
        .post(`/report/ticketsWithMessages`, {
            dateFrom,
            dateTo,
            ...(whatsappIds && whatsappIds.length > 0 ? { whatsappIds } : {})
        })
        .then(({ data }) => data);
};

export const getUsersSyntheticReport = (
    dateFrom: string,
    dateTo: string,
    userId?: number
): Promise<string> => {
    return api
        .post(`/report/usersSynthetic`, { dateFrom, dateTo, userId })
        .then(({ data }) => data);
};

export const getTemplateErrorReportPlot = (
    dateFrom: string,
    dateTo: string
) => {
    return api
        .get('/messagesReport/metaUndelivered/plot', {
            params: { dateFrom, dateTo }
        })
        .then(({ data }) => data);
};

export const getTemplateErrorReportList = (
    dateFrom: string,
    dateTo: string,
    statusError?: string | null,
    pageNumber = 1
) => {
    return api
        .get('/messagesReport/metaUndelivered', {
            params: { dateFrom, dateTo, statusError, pageNumber }
        })
        .then(({ data }) => data);
};

export const getTemplateErrorReportCsv = (
    dateFrom: string,
    dateTo: string,
    statusError?: string | null
): Promise<string> => {
    return api
        .get('/messagesReport/metaUndeliveredReport', {
            params: { dateFrom, dateTo, statusError }
        })
        .then(({ data }) => {
            if (typeof data === 'string') {
                return data;
            }

            const messages = data.messages || [];
            const headers = [
                'ID Ticket',
                'Data Envio',
                'Nome Contato',
                'Numero Contato',
                'Mensagem',
                'Erro'
            ];

            const escapeCsvField = (
                field: string | number | null | undefined
            ) => {
                let str = String(field ?? '');
                str = str
                    .replace(/\r\n/g, ' ')
                    .replace(/\r/g, ' ')
                    .replace(/\n/g, ' ')
                    .replace(/\t/g, ' ')
                    .replace(/\s+/g, ' ')
                    .trim();
                if (
                    str.includes('"') ||
                    str.includes(',') ||
                    str.includes(';')
                ) {
                    return `"${str.replace(/"/g, '""')}"`;
                }
                return str;
            };

            const rows = messages.map(
                (msg: {
                    ticketId: number;
                    createdAt: string;
                    ticket: { contact: { name: string; number: string } };
                    body: string;
                    statusError: string;
                }) => {
                    return [
                        escapeCsvField(msg.ticketId),
                        escapeCsvField(msg.createdAt),
                        escapeCsvField(msg.ticket?.contact?.name),
                        escapeCsvField(msg.ticket?.contact?.number),
                        escapeCsvField(msg.body),
                        escapeCsvField(msg.statusError)
                    ].join(',');
                }
            );

            return [headers.join(','), ...rows].join('\n');
        });
};

export type AuditDetail = {
    id: string;
    companyId: number;
    action: string;
    area: string;
    userId: number;
    userEmail: string;
    description: string;
    payload: string;
    createdAt: string | Date;
    updatedAt: string | Date;
};

export const getAuditById = (id: string | number): Promise<AuditDetail> => {
    return api.get(`/audits/${id}`).then(({ data }) => data);
};

export type GetAuditsParams = {
    pageNumber?: number;
    area?: string;
    action?: string;
    fromDate?: string;
    toDate?: string;
    userId?: number;
};

export const getAudits = (params?: GetAuditsParams) => {
    return api.get('/audits', { params }).then(({ data }) => data);
};
